import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import EditableLoadRow from '../EditableLoadRow';
import { toast } from 'sonner';
import { GripVertical, X } from 'lucide-react';
import { DndContext, closestCorners, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// List view columns configuration
export const ALL_LIST_COLUMNS = [
  { id: 'load_number', label: 'Load #', required: true },
  { id: 'origin', label: 'Origin' },
  { id: 'pu_appt', label: 'PU Appt Date' },
  { id: 'destination', label: 'Destination / Stops' },
  { id: 'del_appt', label: 'DEL Appt Date' },
  { id: 'status', label: 'Status', adminOnly: true },
  { id: 'total_customer', label: 'Total Customer', adminOnly: true },
  { id: 'total_carrier', label: 'Total Carrier', adminOnly: true },
  { id: 'margin', label: 'Margin', adminOnly: true },
];

export const DEFAULT_LIST_COLUMNS = ['load_number', 'origin', 'pu_appt', 'destination', 'del_appt', 'status', 'total_customer', 'total_carrier', 'margin'];
export const RESTRICTED_LIST_COLUMNS = ['load_number', 'origin', 'pu_appt', 'destination', 'del_appt'];
// Migrate column ids saved in localStorage from the previous layout
const LIST_COLUMN_RENAMES = { pu_number: 'pu_appt', po_number: 'del_appt' };
export const migrateListColumns = (cols) => {
  const next = [];
  for (const c of cols) {
    const id = LIST_COLUMN_RENAMES[c] || c;
    if (ALL_LIST_COLUMNS.some(a => a.id === id) && !next.includes(id)) next.push(id);
  }
  return next.length ? next : DEFAULT_LIST_COLUMNS;
};

// List Column Manager - For managing table columns in list view
export const ListColumnManager = ({ activeColumns, onToggleColumn, onClose, allColumns, isRestrictedUser }) => {
  // Filter out admin-only columns for restricted users
  const availableColumns = isRestrictedUser 
    ? allColumns.filter(c => !c.adminOnly)
    : allColumns;
    
  return (
    <>
      {/* Mobile overlay */}
      <div 
        className="fixed inset-0 bg-black/30 z-40 md:hidden" 
        onClick={onClose}
      />
      <div className="fixed md:absolute left-4 right-4 md:left-auto md:right-0 top-1/4 md:top-12 bg-[var(--tms-card)] border rounded-lg shadow-lg p-4 z-50 md:w-64">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-[var(--tms-text)]">Manage Columns</h4>
          <button onClick={onClose} className="p-1 hover:bg-[var(--tms-hover)] rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-[var(--tms-text-muted)] mb-3">Select columns to show. Drag headers to reorder.</p>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {availableColumns.map(column => (
            <label 
              key={column.id} 
              className={`flex items-center gap-2 p-2 rounded ${column.required ? 'opacity-60' : 'cursor-pointer hover:bg-[var(--tms-hover)]'}`}
            >
              <input
                type="checkbox"
                checked={activeColumns.includes(column.id)}
                onChange={() => !column.required && onToggleColumn(column.id)}
                disabled={column.required}
                className="rounded border-[var(--tms-border)]"
              />
              <span className="text-sm text-[var(--tms-text-secondary)]">
                {column.label}
                {column.required && <span className="text-xs text-[var(--tms-text-muted)] ml-1">(required)</span>}
              </span>
            </label>
          ))}
        </div>
      </div>
    </>
  );
};

const DraggableTableHeader = ({ columnId, label, onRemove, isRequired, isRestrictedUser, width, onResize }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: columnId,
  });
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = React.useRef(0);
  const startWidthRef = React.useRef(0);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isResizing ? 'none' : transition,
    opacity: isDragging ? 0.5 : 1,
    width: width ? `${width}px` : 'auto',
    minWidth: '60px',
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width || e.currentTarget.parentElement.offsetWidth;
    
    const handleMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startXRef.current;
      const newWidth = Math.max(60, startWidthRef.current + delta);
      onResize?.(columnId, newWidth);
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <th
      ref={setNodeRef}
      style={style}
      className="group relative select-none"
    >
      <div className="flex items-center gap-1">
        {!isRestrictedUser && (
          <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-[var(--tms-hover)] rounded">
            <GripVertical className="w-3 h-3 text-[var(--tms-text-muted)]" />
          </span>
        )}
        <span className="truncate">{label}</span>
        {!isRequired && !isRestrictedUser && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove(columnId);
            }}
            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 hover:text-red-600 rounded transition-opacity"
            title="Remove column"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {/* Resize handle */}
      <div
        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 group-hover:bg-blue-300/50 transition-colors"
        onMouseDown={handleResizeStart}
        title="Drag to resize"
      />
    </th>
  );
};

const DEFAULT_COLUMN_WIDTHS = {
  load_number: 170, origin: 260, pu_appt: 150, destination: 260, del_appt: 150,
  status: 130, total_customer: 120, total_carrier: 120, margin: 110,
};

// Shared list-column state (order, visibility, widths) persisted in localStorage.
export const useListColumns = (isRestrictedUser) => {
  const [activeListColumns, setActiveListColumns] = useState(() => {
    if (isRestrictedUser) return RESTRICTED_LIST_COLUMNS;
    const saved = localStorage.getItem('tms_list_columns');
    return saved ? migrateListColumns(JSON.parse(saved)) : DEFAULT_LIST_COLUMNS;
  });
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('tms_list_column_widths');
    const merged = saved ? { ...DEFAULT_COLUMN_WIDTHS, ...JSON.parse(saved) } : { ...DEFAULT_COLUMN_WIDTHS };
    ['pu_appt', 'del_appt'].forEach(k => { if ((merged[k] || 0) < 150) merged[k] = 150; });
    return merged;
  });

  useEffect(() => {
    localStorage.setItem('tms_list_column_widths', JSON.stringify(columnWidths));
  }, [columnWidths]);

  const resizeColumn = useCallback((columnId, newWidth) => {
    setColumnWidths(prev => ({ ...prev, [columnId]: Math.max(60, newWidth) }));
  }, []);

  const toggleColumn = (columnId) => {
    const column = ALL_LIST_COLUMNS.find(c => c.id === columnId);
    if (column?.required) return;
    setActiveListColumns(prev => prev.includes(columnId)
      ? (prev.length > 1 ? prev.filter(c => c !== columnId) : prev)
      : [...prev, columnId]);
  };

  const removeColumn = (columnId) => {
    const column = ALL_LIST_COLUMNS.find(c => c.id === columnId);
    if (column?.required) { toast.error('Cannot remove required column'); return; }
    setActiveListColumns(prev => {
      if (prev.length <= 1) { toast.error('Must have at least one column'); return prev; }
      return prev.filter(c => c !== columnId);
    });
  };

  const reorderColumns = (oldIndex, newIndex) => {
    setActiveListColumns(prev => arrayMove(prev, oldIndex, newIndex));
  };

  return { activeListColumns, setActiveListColumns, columnWidths, resizeColumn, toggleColumn, removeColumn, reorderColumns };
};

const pickupDateKey = (load) => {
  const pickups = (load.stops || []).filter(s => s.type === 'pickup');
  const dt = pickups.map(s => s.appointment_datetime).filter(Boolean).sort()[0] || load.shipping_date;
  return dt ? String(dt).split('T')[0] : null;
};

const groupByPickupDate = (loads) => {
  const groups = new Map();
  loads.forEach(l => {
    const key = pickupDateKey(l) || 'none';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(l);
  });
  const keys = [...groups.keys()].filter(k => k !== 'none').sort();
  if (groups.has('none')) keys.push('none');
  return keys.map(k => ({ key: k, loads: groups.get(k) }));
};

// Within a day, order loads so each customer's loads sit together (customers alphabetical, No Customer last)
const sortByCustomer = (loads) => [...loads].sort((a, b) => {
  const an = a.customer_name || '', bn = b.customer_name || '';
  return (!an) - (!bn) || an.localeCompare(bn) || (a.customer_id || '').localeCompare(b.customer_id || '');
});

const formatGroupDate = (key) => {
  if (key === 'none') return 'No Pickup Date';
  const d = new Date(`${key}T00:00:00`);
  if (isNaN(d)) return key;
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
};

// Editable spreadsheet of loads grouped by pickup date (shared by Loads page and Customer profile)
export const LoadsSpreadsheet = ({ loads, facilities, customers, isRestrictedUser, listColumns, onLoadSaved, onLoadDuplicated, fillHeight = false, emptyMessage = 'No loads yet. Create your first load to get started.' }) => {
  const navigate = useNavigate();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const { activeListColumns, columnWidths, resizeColumn, removeColumn, reorderColumns } = listColumns;
  const visibleCount = activeListColumns.filter(c => {
    const col = ALL_LIST_COLUMNS.find(ac => ac.id === c);
    return col && (!col.adminOnly || !isRestrictedUser);
  }).length + 1;
  const dayGroups = groupByPickupDate(loads).map(g => ({ ...g, loads: sortByCustomer(g.loads) }));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragEnd={({ active, over }) => {
        if (active && over && active.id !== over.id) {
          const oldIndex = activeListColumns.indexOf(active.id);
          const newIndex = activeListColumns.indexOf(over.id);
          if (oldIndex !== -1 && newIndex !== -1) reorderColumns(oldIndex, newIndex);
        }
      }}
    >
      <div className={fillHeight ? 'flex-1 min-h-0 overflow-auto' : 'overflow-x-auto'} data-testid="loads-table-scroll">
        <table className="data-table w-full" style={{ tableLayout: 'auto' }} data-testid="loads-table">
          <thead className="sticky-thead">
            <SortableContext items={activeListColumns} strategy={horizontalListSortingStrategy}>
              <tr>
                {activeListColumns.map(columnId => {
                  const column = ALL_LIST_COLUMNS.find(c => c.id === columnId);
                  if (!column || (column.adminOnly && isRestrictedUser)) return null;
                  return (
                    <DraggableTableHeader
                      key={columnId}
                      columnId={columnId}
                      label={column.label}
                      onRemove={removeColumn}
                      isRequired={column.required}
                      isRestrictedUser={isRestrictedUser}
                      width={columnWidths[columnId]}
                      onResize={resizeColumn}
                    />
                  );
                })}
                <th className="text-right px-3 text-xs font-semibold text-[var(--tms-text-secondary)]" style={{ width: '140px', minWidth: '140px' }}>Actions</th>
              </tr>
            </SortableContext>
          </thead>
          <tbody>
            {loads.length === 0 ? (
              <tr>
                <td colSpan={visibleCount} className="text-center py-12 text-[var(--tms-text-muted)]">{emptyMessage}</td>
              </tr>
            ) : (
              dayGroups.map((group) => (
                <React.Fragment key={group.key}>
                  <tr data-testid="pickup-date-group-header" data-day-key={group.key}>
                    <td colSpan={visibleCount} style={{ background: '#f2f4f9', borderTop: '2px solid var(--tms-border)', padding: '6px 12px' }}>
                      <span className="text-xs font-bold uppercase tracking-wide text-[var(--tms-text-secondary)]">{formatGroupDate(group.key)}</span>
                      <span className="text-xs text-[var(--tms-text-muted)] ml-2">{group.loads.length} load{group.loads.length !== 1 ? 's' : ''}</span>
                    </td>
                  </tr>
                  {group.loads.map((load) => (
                    <EditableLoadRow
                      key={load.id}
                      load={load}
                      facilities={facilities}
                      customers={customers}
                      onSave={onLoadSaved}
                      onDuplicate={(newLoad) => { onLoadDuplicated?.(newLoad); navigate(`/loads/${newLoad.id}`); }}
                      onNavigate={(loadId) => navigate(`/loads/${loadId}`)}
                      isRestrictedUser={isRestrictedUser}
                      activeColumns={activeListColumns}
                      allColumns={ALL_LIST_COLUMNS}
                      columnWidths={columnWidths}
                    />
                  ))}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DndContext>
  );
};
